import React, { useEffect, useState } from 'react';
import { api, AutoRule } from './api';
import { Select } from './Select';

const ROLES = ['owner', 'admin', 'member', 'guest'];
const REMOVE: React.CSSProperties = {
  background: 'none', border: 'none', color: '#e56a6a', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
};

/** Role/field auto-membership rules for a channel (admins). Members matching a rule are
 *  auto-added and removed when they stop matching; manual members are never touched. */
export function AutoRules({ channelId, workspaceId }: { channelId: string; workspaceId?: string }) {
  const [rules, setRules] = useState<AutoRule[]>([]);
  const [fields, setFields] = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [ruleType, setRuleType] = useState<'role' | 'field'>('role');
  const [role, setRole] = useState('member');
  const [fieldId, setFieldId] = useState('');
  const [value, setValue] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.listAutoRules(channelId).catch(() => [] as AutoRule[]),
      workspaceId ? api.listFields(workspaceId).catch(() => []) : Promise.resolve([]),
    ]).then(([r, f]) => {
      if (!alive) return;
      setRules(r);
      setFields((f as any[]).map((x) => ({ id: x.id, label: x.label })));
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [channelId, workspaceId]);

  const label = (r: AutoRule) => {
    if (r.rule_type === 'role') return `Everyone with role “${r.match_value}”`;
    const f = fields.find((x) => x.id === r.field_id);
    return `Everyone with ${f ? f.label : 'a field'} = “${r.match_value}”`;
  };
  const add = async () => {
    if (ruleType === 'field' && (!fieldId || !value.trim())) return;
    try {
      const body = ruleType === 'role'
        ? { rule_type: 'role', match_value: role }
        : { rule_type: 'field', field_id: fieldId, match_value: value.trim() };
      const r = await api.createAutoRule(channelId, body);
      setRules((c) => [...c, r]);
      setAdding(false); setValue('');
    } catch (e: any) { window.alert(e?.message || 'Could not add the rule.'); }
  };
  const remove = async (r: AutoRule) => {
    if (!window.confirm('Remove this rule? Anyone added only by it will leave the channel.')) return;
    try { await api.deleteAutoRule(r.id); setRules((c) => c.filter((x) => x.id !== r.id)); } catch { /* ignore */ }
  };

  if (loading) return <div className="fx-dlg-value">Loading…</div>;
  return (
    <div>
      {rules.length === 0 ? (
        <div className="fx-dlg-value" style={{ color: 'var(--fx-muted,#9092a0)' }}>No rules — members join manually.</div>
      ) : (
        rules.map((r) => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderBottom: '1px solid rgba(128,128,128,0.14)' }}>
            <span style={{ flex: 1, fontSize: 13 }}>{label(r)}</span>
            <button type="button" onClick={() => remove(r)} style={REMOVE}>Remove</button>
          </div>
        ))
      )}
      {adding ? (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <Select value={ruleType} onChange={(v) => setRuleType(v as 'role' | 'field')} style={{ minWidth: 108 }}
            options={[{ value: 'role', label: 'By role' }, { value: 'field', label: 'By field', disabled: fields.length === 0 }]} />
          {ruleType === 'role' ? (
            <Select value={role} onChange={setRole} style={{ minWidth: 108 }} options={ROLES.map((r) => ({ value: r, label: r }))} />
          ) : (
            <>
              <Select value={fieldId} onChange={setFieldId} placeholder="Field…" style={{ minWidth: 130 }}
                options={fields.map((f) => ({ value: f.id, label: f.label }))} />
              <span style={{ fontSize: 13 }}>=</span>
              <input className="fx-text-input" style={{ width: 140 }} value={value} onChange={(e) => setValue(e.target.value)} placeholder="value" />
            </>
          )}
          <button type="button" className="fx-sendbtn" onClick={add}>Add</button>
          <button type="button" onClick={() => setAdding(false)} style={{ ...REMOVE, color: 'var(--fx-accent,#f7b500)' }}>Cancel</button>
        </div>
      ) : (
        <button type="button" className="fx-login-back" style={{ marginTop: 8 }} onClick={() => setAdding(true)}>+ Add a rule</button>
      )}
    </div>
  );
}
